import { useState } from 'react';
import { Link } from 'react-router-dom';

import { getTriggerEventInfo } from './utils';
import queryString from 'query-string';

import { Location } from '@cobliteam/common-types';

import type { TriggerEventNotification } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/events';

import { formatYesterdayOrToday, getTime } from '@cobliteam/utils/date';

import { useMarkNotificationAsRead } from '@cobliteam/hooks/src/useEntity/useAlerts';

import { TripsIcon } from '@cobliteam/base-components';

import { Button, Typography } from '@cobliteam/ui/components';
import cn from '@cobliteam/ui/utils/cn';

import CircleRoundedIcon from '@mui/icons-material/CircleRounded';

interface TriggerNotificationItemProps {
  triggerNotification: TriggerEventNotification;
  isRead: boolean;
}

const TriggerNotificationItem = ({ triggerNotification, isRead }: TriggerNotificationItemProps) => {
  const { createdAt, readAt, triggerEvent, id } = triggerNotification;
  const { trigger, eventTime, meta, vehicleId, id: eventId } = triggerEvent;

  const [markAsRead, setMarkAsRead] = useState(Boolean(readAt) || isRead);

  const { mutate } = useMarkNotificationAsRead(id, createdAt);

  if (!meta.conditionCategory) return null;

  const { name, description, icon, type } = getTriggerEventInfo(triggerEvent);

  const eventType = {
    geofence: 'geofence',
    'idle-engine': 'stop',
    'device-connection': 'incident',
    'vehicle-speed-limit': 'speed',
    'road-speed-limit': 'speed',
  }[type];

  const params = {
    vehicle_id: vehicleId,
    date: getTime(new Date(eventTime), 'start'),
    event_type: eventType,
    event_id: eventId,
    event_time: getTime(new Date(eventTime), 'start'),
  };

  const paramsUrl = queryString.stringify(params);

  const handleMarkAsRead = () => {
    if (markAsRead) return;

    mutate();
    setMarkAsRead(true);
  };

  return (
    <div className={cn('p-2.5 m-2.5 rounded space-y-2.5 border border-gray-light', markAsRead ? 'hover:bg-gray-lightest' : 'bg-primary-lightness')} onMouseEnter={handleMarkAsRead}>
      <div className='flex justify-between gap-2.5 items-center'>
        <div className='flex items-baseline gap-1'>
          {markAsRead ? undefined : <CircleRoundedIcon className='size-2.5 text-primary' />}
          <Typography variant='caption-medium' color='secondary'>
            Regra ativada {name}
          </Typography>
        </div>
        <Typography variant='chip-label' color='secondary'>
          {formatYesterdayOrToday(Date.now(), new Date(eventTime).valueOf(), '-')}
        </Typography>
      </div>

      <div className='flex items-center gap-1'>
        {icon}
        <Typography variant='description-title' color={markAsRead ? 'secondary' : 'primary'}>
          {trigger.name}
        </Typography>
      </div>

      <Typography variant='caption-default' color='secondary' className='ml-5'>
        O motorista <b>{meta.driver?.name}</b> {description}
      </Typography>

      <Link
        to={{
          pathname: Location.TRACKING,
          search: `?${paramsUrl}`,
        }}
        target='_blank'
        rel='noopener noreferrer'
        className='flex w-fit ml-auto'
      >
        <Button variant='outlined' color='secondary' size='condensed' className='group'>
          <TripsIcon className='size-4' />
          <span className='w-0 overflow-hidden group-hover:w-20 transition-all ease-linear duration-150 ml-0 group-hover:ml-1'>Ver trajeto</span>
        </Button>
      </Link>
    </div>
  );
};

export default TriggerNotificationItem;
